// frontend/src/pages/AuditLog/AuditLogsList.jsx
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Eye, Trash2, Filter, Search, RefreshCw,
  Loader2, Globe, Building, User, ChevronLeft, ChevronRight,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import "./AuditLogsList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const PAGE_SIZE = 10;

const AuditLogsList = () => {
  const { currentUser, userType } = useAuth();
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // filters / search
  const [searchInput, setSearchInput] = useState("");   // what user types
  const [searchTerm, setSearchTerm] = useState("");     // committed value
  const [selectedModule, setSelectedModule] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // dropdown options (from /filters endpoint)
  const [moduleOptions, setModuleOptions] = useState([]);
  const [actionOptions, setActionOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  // pagination
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    page: 1, limit: PAGE_SIZE, total: 0, total_pages: 1,
  });

  const isSystemAdmin =
    userType === "system" &&
    (currentUser?.role === "developer" || currentUser?.role === "admin");

  // ------------------------------------------------------------------
  // Fetch list
  // ------------------------------------------------------------------
  const fetchLogs = useCallback(
    async (opts = {}) => {
      const { silent = false } = opts;
      try {
        silent ? setRefreshing(true) : setLoading(true);

        const params = { page, limit: PAGE_SIZE };
        if (selectedModule) params.module = selectedModule;
        if (selectedAction) params.action = selectedAction;
        if (selectedStatus) params.status = selectedStatus;
        if (searchTerm)     params.search = searchTerm;

        const res = await axios.get(`${url}/api/audit-logs`, {
          headers: getAuthHeaders(),
          params,
        });

        if (res.data.success) {
          setLogs(res.data.data || []);
          setMeta(
            res.data.meta || {
              page: 1, limit: PAGE_SIZE, total: 0, total_pages: 1,
            }
          );
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to fetch audit logs"
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [page, selectedModule, selectedAction, selectedStatus, searchTerm]
  );

  // ------------------------------------------------------------------
  // Fetch filter dropdown values
  // ------------------------------------------------------------------
  const fetchFilters = async () => {
    try {
      const res = await axios.get(`${url}/api/audit-logs/filters`, {
        headers: getAuthHeaders(),
      });
      if (res.data.success) {
        setModuleOptions(res.data.data.modules || []);
        setActionOptions(res.data.data.actions || []);
        setStatusOptions(res.data.data.statuses || []);
      }
    } catch (error) {
      // non-fatal — filters just stay empty
      console.warn("Filter options unavailable:", error?.message);
    }
  };

  // ------------------------------------------------------------------
  // Effects
  // ------------------------------------------------------------------
  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Reset to page 1 whenever filters/search change
  useEffect(() => {
    setPage(1);
  }, [selectedModule, selectedAction, selectedStatus, searchTerm]);

  // ------------------------------------------------------------------
  // Handlers
  // ------------------------------------------------------------------
  const handleSearch = (e) => {
    e?.preventDefault();
    setPage(1);
    setSearchTerm(searchInput.trim());
  };

  const handleClearFilters = () => {
    setSelectedModule("");
    setSelectedAction("");
    setSelectedStatus("");
    setSearchInput("");
    setSearchTerm("");
    setPage(1);
  };

  const handleRefresh = () => fetchLogs({ silent: true });

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this audit log?")) return;
    try {
      await axios.delete(`${url}/api/audit-logs/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Audit log deleted");
      fetchLogs({ silent: true });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete audit log");
    }
  };

  // ------------------------------------------------------------------
  // Formatting
  // ------------------------------------------------------------------
  const formatDateTime = (d) =>
    d
      ? new Date(d).toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "-";

  const getBadgeClass = (action) => {
    switch ((action || "").toUpperCase()) {
      case "LOGIN":  return "badge success";
      case "LOGOUT": return "badge neutral";
      case "CREATE": return "badge info";
      case "UPDATE": return "badge warning";
      case "DELETE": return "badge danger";
      default:       return "badge neutral";
    }
  };

  const startNo = (meta.page - 1) * meta.limit;

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="spin" size={28} />
        <div className="loading">Loading audit logs...</div>
      </div>
    );
  }

  return (
    <div className="auditlog-container">
      {/* Header */}
      <div className="header">
        <div>
          <h2>Audit Logs</h2>
          <p className="info">Track user activities and system changes.</p>
          <p className="scope">
            {isSystemAdmin ? (
              <><Globe size={14} /> All Companies</>
            ) : (
              <><Building size={14} /> Your Company Activity</>
            )}
          </p>
        </div>

        <div className="header-actions">
          <button
            className="filter-btn"
            onClick={() => setShowFilters((v) => !v)}
          >
            <Filter size={16} /> Filter
          </button>
          <button
            className="refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw size={16} className={refreshing ? "spin" : ""} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="filters-container">
          <select
            value={selectedModule}
            onChange={(e) => setSelectedModule(e.target.value)}
          >
            <option value="">All Modules</option>
            {moduleOptions.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>

          <select
            value={selectedAction}
            onChange={(e) => setSelectedAction(e.target.value)}
          >
            <option value="">All Actions</option>
            {actionOptions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="">All Statuses</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <button className="clear-filters" onClick={handleClearFilters}>
            Clear Filters
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="stats-bar">
        <span>Total: {meta.total} logs</span>
        <span>
          User:{" "}
          {currentUser?.name ||
            currentUser?.Usercomp_sure_name ||
            "You"}
        </span>
      </div>

      {/* Search */}
      <form className="search-container" onSubmit={handleSearch}>
        <input
          type="text"
          placeholder="Search by description, action, module or user..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="search-input"
        />
        <button type="submit" className="search-btn">
          <Search size={16} />
        </button>
      </form>

      {/* Table */}
      <table className="auditlog-table">
        <thead>
          <tr>
            <th>No</th>
            <th>User</th>
            <th>Role</th>
            <th>Action</th>
            <th>Module</th>
            <th>Description</th>
            <th>IP Address</th>
            <th>Date</th>
            <th style={{ textAlign: "center" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {logs.length === 0 ? (
            <tr>
              <td colSpan="9" className="no-data">
                No audit logs found.
              </td>
            </tr>
          ) : (
            logs.map((log, index) => (
              <tr key={log._id || `${log.no}-${index}`}>
                <td>{log.no ?? startNo + index + 1}</td>
                <td>
                  <div className="user-info">
                    <User size={12} />
                    <span>{log.user || "Anonymous"}</span>
                  </div>
                </td>
                <td>{log.role || "-"}</td>
                <td>
                  <span className={getBadgeClass(log.action)}>
                    {log.action}
                  </span>
                </td>
                <td>{log.module}</td>
                <td title={log.description}>{log.description || "-"}</td>
                <td>{log.ip_address || "-"}</td>
                <td>{formatDateTime(log.date)}</td>
                <td className="actions">
                  <button
                    onClick={() => navigate(`/audit-logs/view/${log._id}`)}
                    className="view-btn"
                    title="View Details"
                  >
                    <Eye size={14} />
                  </button>
                  {isSystemAdmin && (
                    <button
                      onClick={() => handleDelete(log._id)}
                      className="delete-btn"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Pagination */}
      {meta.total_pages > 1 && (
        <div className="pagination">
          <button
            disabled={meta.page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft size={16} /> Prev
          </button>

          <span>
            Page {meta.page} of {meta.total_pages} &nbsp;•&nbsp;{" "}
            {meta.total} total
          </span>

          <button
            disabled={meta.page >= meta.total_pages}
            onClick={() => setPage((p) => Math.min(meta.total_pages, p + 1))}
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditLogsList;